import { useTranslations } from 'next-intl';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch } from '@/app/hooks';
import { getImpactImRef, getXhsClickId } from '@/app/utils';
import { openOrRedirect } from '@/app/utils/client';
import { usersThirdPartyLogin } from '../apis';
import { LOGIN_ERROR_INTL_PREFIX } from '../constants';
import type { ThirdPartyLoginAppType } from '../types';

interface UseThirdPartyLoginProps {
  onThirdPartyLoginSuccess: () => void;
  onThirdPartyPopupOpened?: (popup: Window | null) => void;
}

interface ThirdPartyLoginParams {
  url: string;
  type: ThirdPartyLoginAppType;
  inviteCode: string;
  inviter_id?: string;
  device_id: string;
  receive_marketing_msg: boolean | undefined;
  openInPopup?: boolean;
}

export const useThirdPartyLogin = ({ onThirdPartyLoginSuccess, onThirdPartyPopupOpened }: UseThirdPartyLoginProps) => {
  const t = useTranslations();
  const { loading: thirdPartyLoading, run: signInWithThirdParty } = useFetch(
    (params: ThirdPartyLoginParams) => {
      const { url, type, inviteCode, inviter_id, device_id, receive_marketing_msg } = params;
      return usersThirdPartyLogin({
        query: {
          app: type,
          url,
          device_id,
          invite_code: inviteCode,
          inviter_id,
          receive_marketing_msg,
          im_ref: getImpactImRef(),
          xhs_click_id: getXhsClickId(),
        },
      });
    },
    {
      manual: true,
      onSuccess: ({ data, error }, [params]) => {
        if (error) {
          const msg =
            error.reason === 'UserBlocked'
              ? t(`${LOGIN_ERROR_INTL_PREFIX}banedUser`)
              : t(`${LOGIN_ERROR_INTL_PREFIX}${params.type}LoginError`);
          toast({
            type: 'warning',
            message: msg,
          });
          return;
        }
        if (data) {
          onThirdPartyLoginSuccess();
          if (params.openInPopup) {
            const popup = window.open(data.redirect_url, 'vidu-third-party-login', 'popup=yes,width=520,height=720');
            onThirdPartyPopupOpened?.(popup);

            if (!popup) {
              openOrRedirect(data.redirect_url, { target: '_self' });
            }
            return;
          }

          openOrRedirect(data.redirect_url, { target: '_self' });
        }
      },
    },
  );

  return {
    thirdPartyLoading,
    signInWithThirdParty,
  };
};
